import React, { useState } from "react";
import { authService } from "../services/authService";

function Signup({ onSignupSuccess, onSwitchToLogin }) {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
    address: "",
    companyName: "",
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [codeSent, setCodeSent] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationLoading, setVerificationLoading] = useState(false);
  const [verifyCodeLoading, setVerifyCodeLoading] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const resetVerificationState = () => {
    setVerificationCode("");
    setVerificationToken("");
    setCodeSent(false);
    setEmailVerified(false);
    setInfoMessage("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "email") {
      resetVerificationState();
    }
  };

  const handleSendVerificationCode = async () => {
    if (!formData.email.trim()) {
      setError("Please enter your email first");
      return;
    }

    setError("");
    setInfoMessage("");
    setVerificationLoading(true);

    const result = await authService.sendSignupVerificationCode(formData.email);

    if (result.success) {
      setCodeSent(true);
      setInfoMessage(result.message || "Verification code sent. Please check your inbox.");
    } else {
      setError(result.message || "Unable to send verification code");
    }

    setVerificationLoading(false);
  };

  const handleVerifyCode = async () => {
    if (!verificationCode.trim()) {
      setError("Please enter the verification code");
      return;
    }

    setError("");
    setInfoMessage("");
    setVerifyCodeLoading(true);

    const result = await authService.verifySignupCode(formData.email, verificationCode);

    if (result.success) {
      setEmailVerified(true);
      setVerificationToken(result.verificationToken || "");
      setInfoMessage(result.message || "Email verified successfully");
    } else {
      setError(result.message || "Unable to verify email");
    }

    setVerifyCodeLoading(false);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");

    if (!emailVerified || !verificationToken) {
      setError("Please verify your email before signing up");
      return;
    }

    setLoading(true);

    const result = await authService.signup({
      ...formData,
      verificationToken,
    });

    if (result.success) {
      onSignupSuccess(result);
    } else {
      setError(result.message || "Signup failed");
    }

    setLoading(false);
  };

  return (
    <div className="login-form signup-form">
      <p className="signup-intro">
        Verify your email first, then complete your account details.
      </p>

      <form onSubmit={handleSignup} className="login-form-fields signup-form-fields">
        <div className="login-field signup-field">
          <label htmlFor="signup-email">Email</label>
          <div className="signup-inline-row">
            <input
              id="signup-email"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              disabled={emailVerified}
              placeholder="Enter your email"
            />
            <button
              type="button"
              onClick={handleSendVerificationCode}
              disabled={verificationLoading || emailVerified}
              className="signup-secondary-button"
              style={{
                opacity: verificationLoading || emailVerified ? 0.6 : 1,
                cursor:
                  verificationLoading || emailVerified ? "not-allowed" : "pointer",
              }}
            >
              {verificationLoading ? "Sending..." : codeSent ? "Resend Code" : "Send Code"}
            </button>
          </div>
        </div>

        {codeSent && !emailVerified && (
          <div className="login-field signup-field">
            <label htmlFor="signup-verification-code">Verification Code</label>
            <div className="signup-inline-row">
              <input
                id="signup-verification-code"
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value)}
                placeholder="Enter the 6-digit code"
              />
              <button
                type="button"
                onClick={handleVerifyCode}
                disabled={verifyCodeLoading}
                className="signup-secondary-button"
                style={{
                  opacity: verifyCodeLoading ? 0.6 : 1,
                  cursor: verifyCodeLoading ? "not-allowed" : "pointer",
                }}
              >
                {verifyCodeLoading ? "Verifying..." : "Verify"}
              </button>
            </div>
          </div>
        )}

        {infoMessage && (
          <div className={emailVerified ? "signup-message success" : "signup-message info"}>
            {infoMessage}
          </div>
        )}

        <div className="signup-fields-grid">
          <div className="login-field signup-field">
            <label htmlFor="signup-first-name">First Name</label>
            <input
              id="signup-first-name"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              required
              disabled={!emailVerified}
              placeholder="Enter your first name"
            />
          </div>

          <div className="login-field signup-field">
            <label htmlFor="signup-last-name">Last Name</label>
            <input
              id="signup-last-name"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              required
              disabled={!emailVerified}
              placeholder="Enter your last name"
            />
          </div>
        </div>

        <div className="login-field signup-field">
          <label htmlFor="signup-password">Password</label>
          <input
            id="signup-password"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            required
            disabled={!emailVerified}
            placeholder="Enter your password"
          />
        </div>

        <div className="login-field signup-field">
          <label htmlFor="signup-address">Address</label>
          <input
            id="signup-address"
            type="text"
            name="address"
            value={formData.address}
            onChange={handleChange}
            disabled={!emailVerified}
            placeholder="Enter your address"
          />
        </div>

        <div className="login-field signup-field">
          <label htmlFor="signup-company-name">Company Name</label>
          <input
            id="signup-company-name"
            type="text"
            name="companyName"
            value={formData.companyName}
            onChange={handleChange}
            disabled={!emailVerified}
            placeholder="Enter your company name"
          />
        </div>

        {error && <div className="login-error signup-message error">{error}</div>}

        <button
          type="submit"
          disabled={loading || !emailVerified}
          className="login-submit signup-submit"
          style={{
            opacity: loading || !emailVerified ? 0.6 : 1,
            cursor: loading || !emailVerified ? "not-allowed" : "pointer",
          }}
        >
          {loading ? "Signing up..." : "Sign Up"}
        </button>
      </form>

      <p className="login-switch-text signup-switch-text">
        Already have an account?{" "}
        <button onClick={onSwitchToLogin} className="login-switch-link signup-switch-link">
          Login here
        </button>
      </p>
    </div>
  );
}

export default Signup;
